import { cn } from '@/lib/utils';
import { chart } from '@/theme';
import { formatCount, formatPorcentaje, type PasoEmbudo } from '../reports-format';

/**
 * Embudo «conversaciones → con orden → ventas confirmadas» en barras horizontales (CSS puro).
 *
 * Tres decisiones:
 *  - el ancho es el PORCENTAJE sobre el primer escalón (100 % de las conversaciones), no la
 *    proporción contra el máximo: así «40 %» en pantalla es el mismo número que enseña el backend y
 *    el escalón que se encoge se ve encogerse de verdad;
 *  - un porcentaje `null` (periodo sin conversaciones) deja la barra en cero con el texto «—»: cero y
 *    «no se puede calcular» no son lo mismo, y acá el hueco se explica en el pie del escalón;
 *  - los colores son los tres primeros de `chart.series` (azul → violeta → verde): el orden cuenta
 *    la historia sin necesidad de leyenda.
 */

export interface EmbudoBarsProps {
  pasos: PasoEmbudo[];
  className?: string;
}

export function EmbudoBars({ pasos, className }: EmbudoBarsProps) {
  if (pasos.length === 0) return null;

  return (
    <ol className={cn('space-y-4', className)}>
      {pasos.map((paso, indice) => {
        // 2% mínimo para que un escalón con datos no se vea como una barra vacía.
        const ancho =
          paso.porcentaje === null ? 0 : Math.max(2, Math.min(100, Math.round(paso.porcentaje)));

        return (
          <li key={paso.id}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-body font-medium text-ink">{paso.label}</span>
              <span className="tabular shrink-0 text-body font-semibold text-ink-soft">
                {formatCount(paso.valor)}
              </span>
            </div>

            <div className="mt-1.5 h-2 w-full overflow-hidden rounded-pill bg-surface-muted">
              <span
                className="block h-full rounded-pill"
                style={{
                  width: `${ancho}%`,
                  backgroundColor: chart.series[indice % chart.series.length],
                }}
              />
            </div>

            <p className="mt-1 flex flex-wrap items-baseline gap-x-2 text-micro text-ink-faint">
              <span className="tabular font-medium text-ink-muted">
                {formatPorcentaje(paso.porcentaje, 0)}
              </span>
              <span>{paso.detalle}</span>
            </p>
          </li>
        );
      })}
    </ol>
  );
}
